import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Activity, ActivityKind, TimelineScope } from "@/api/queries";
import { TIMELINE_PAGE_SIZE, useTimeline } from "@/api/queries";
import { CHANNEL_BAR, channelOf } from "@/agenda/model";
import { can, useMe } from "@/auth/useMe";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Select,
  Spinner,
  Tag,
} from "@/components/ui";
import { Pagination } from "@/components/Pagination";
import { formatAgo, formatDateTime } from "@/lib/format";
import { recordingUrl, useCallsForActivities } from "@/telephony/queries";
import type { Call } from "@/telephony/state";
import { formatDuration, phaseFromServer } from "@/telephony/state";

/** `Activity.source_model` of an entry the dialler wrote (D9). */
const CALL_SOURCE = "telephony.call";

/**
 * Every kind a history can hold, in integer order. All sixteen, not the ones
 * *Agregar seguimiento* offers: fifteen years of rows include kinds nobody
 * creates by hand any more, like 10, *Importación de datos*.
 */
const KINDS = Array.from({ length: 16 }, (_, kind) => kind) as ActivityKind[];

function PhoneIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}

/**
 * What a call entry says in place of a comment nobody typed: who was called and
 * for how long. In the accent, like the *automático* beside the kind — the
 * system's voice, as against the agent's.
 *
 * And how it ended, when nobody picked up: every call is on the history, the
 * ones that rang out included, and the duration of those is ringing — which
 * without the word beside it reads as half a minute of conversation.
 */
function CallLine({ activity, call }: { activity: Activity; call: Call }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [listening, setListening] = useState(false);
  // The permission, not the role: `play_recording` is granted apart from
  // seeing calls (.docs/06 §5), and to management only — a seller reads who
  // was called and for how long, and is offered nothing to play. Whose calls
  // is the server's half: managers are handed every call.
  const mayListen = call.has_recording && can(me, "telephony.play_recording");

  return (
    <div className="mt-1">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-brand-700">
        <PhoneIcon />
        <span className="font-medium">
          {activity.contact_name || call.dialed_number}
        </span>
        {phaseFromServer(call.status) === "failed" ? (
          <span>{t(`timeline.callStatus.${call.status}`)}</span>
        ) : null}
        {call.duration ? (
          <span>
            {t("timeline.callDuration", { time: formatDuration(call.duration) })}
          </span>
        ) : null}
        {mayListen && !listening ? (
          <Button variant="ghost" onClick={() => setListening(true)}>
            <SpeakerIcon />
            {t("timeline.listen")}
          </Button>
        ) : null}
      </p>
      {/* Mounted on the click and not before. Every request for the audio
          writes a `RecordingAccessLog` row, and a page that preloaded its
          recordings would log listens nobody made. */}
      {listening ? (
        <audio
          controls
          autoPlay
          preload="none"
          src={recordingUrl(call.ref, t("timeline.recordingReason"))}
          className="mt-2 block w-full max-w-sm"
        />
      ) : null}
    </div>
  );
}

function Entry({
  activity,
  call,
  awaitingCall,
  showContact,
}: {
  activity: Activity;
  /** The call that wrote this entry, if one did and the reader may open it. */
  call: Call | undefined;
  /** That call is still being looked up. */
  awaitingCall: boolean;
  /** Name who the entry is about. Off on a contact's own history. */
  showContact: boolean;
}) {
  const { t } = useTranslation();
  const isScheduled = !activity.is_completed && Boolean(activity.due_at);
  // A call entry's comment is the server's summary unless the agent wrote the
  // call up, and the call line already says what the summary says. So under
  // the line it shows only as notes — and in full wherever the line cannot be
  // drawn: a failed lookup, or a call this reader may not open.
  const comment = call
    ? call.notes && activity.comment
    : awaitingCall
      ? ""
      : activity.comment;

  return (
    <li className="flex gap-3 border-b border-rule px-4 py-3 last:border-0">
      {/* The channel bar of D24, the same mapping the agenda draws from. The
          thirteen hues this replaces encoded a funnel the model never had —
          `agenda/model.ts` owns the rule so the two cannot drift. */}
      <span
        aria-hidden="true"
        className="mt-1 w-[3px] shrink-0 self-stretch"
        style={{ background: CHANNEL_BAR[channelOf(activity.kind)] }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-base font-bold">{activity.kind_display}</span>
          {/* Who wrote it, beside when: `created_by_agent`, never
              `current_agent` — that is who holds the row now, and it moves
              with the account on a claim. A darker grey than the rest of the
              screen's meta — it is read on every row, not glanced at. */}
          <span className="text-xs text-mute-700">
            {activity.created_by_agent_name
              ? t("timeline.createdBy", {
                  when: formatDateTime(activity.created),
                  ago: formatAgo(activity.created),
                  agent: activity.created_by_agent_name,
                })
              : t("timeline.created", {
                  when: formatDateTime(activity.created),
                  ago: formatAgo(activity.created),
                })}
          </span>
          {/* The row came from a call or a conversation, not from someone
              typing it (D9). Worth saying, because it changes how much the
              comment can be trusted to be complete — and worth the accent, so
              it reads at a glance down a long history. */}
          {activity.source_model ? (
            <span className="text-xs text-brand-700">{t("timeline.automatic")}</span>
          ) : null}
        </div>
        {call ? <CallLine activity={activity} call={call} /> : null}
        {/* Who at the company it was with. A company's history runs across
            several people, and the comment rarely names the one. Not under a
            call line, which already leads with the name — and not while that
            line is still being looked up, or the name would be drawn here and
            then again there. */}
        {showContact && activity.contact_name && !call && !awaitingCall ? (
          <p className="mt-1 text-sm">
            <span className="text-mute-700">{t("timeline.contact")}</span>{" "}
            <span className="font-medium">{activity.contact_name}</span>
          </p>
        ) : null}
        {comment ? <p className="mt-1 text-sm whitespace-pre-wrap">{comment}</p> : null}
        {isScheduled ? (
          <p className="mt-1 text-xs text-mute-600">
            {t("timeline.reminder", { when: formatDateTime(activity.due_at) })}
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The kind picker in the corner of the timeline's own header. A select, not the
 * system's segmented control: thirteen kinds do not fit on a row, least of all
 * at 390px.
 */
function TimelineKindFilter({
  value,
  onChange,
}: {
  value: ActivityKind | null;
  onChange: (kind: ActivityKind | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <Select
      aria-label={t("timeline.kindFilter")}
      className="w-auto"
      value={value ?? ""}
      onChange={(event) =>
        onChange(
          event.target.value === ""
            ? null
            : (Number(event.target.value) as ActivityKind),
        )
      }
    >
      <option value="">{t("timeline.allKinds")}</option>
      {KINDS.map((kind) => (
        <option key={kind} value={kind}>
          {t(`activityKind.${kind}`)}
        </option>
      ))}
    </Select>
  );
}

export function Timeline({ scope }: { scope: TimelineScope }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  /** One kind only, or `null` for every kind. */
  const [kind, setKind] = useState<ActivityKind | null>(null);
  const { data, isPending, isError, isFetching, refetch } = useTimeline(
    scope,
    page,
    true,
    kind,
    TIMELINE_PAGE_SIZE,
  );
  const calls = useCallsForActivities(
    (data?.results ?? [])
      .filter((activity) => activity.source_model === CALL_SOURCE)
      .map((activity) => activity.id),
  );

  return (
    <Card
      title={t("timeline.title")}
      actions={
        <TimelineKindFilter
          value={kind}
          onChange={(next) => {
            setKind(next);
            // Page 4 of everything is not page 4 of calls, and may not exist.
            setPage(1);
          }}
        />
      }
    >
      {isPending ? (
        <Spinner label={t("timeline.loading")} />
      ) : isError ? (
        <ErrorState message={t("timeline.loadFailed")} onRetry={() => void refetch()} />
      ) : data.results.length === 0 ? (
        <EmptyState>
          {kind === null ? t("timeline.empty") : t("timeline.emptyFiltered")}
        </EmptyState>
      ) : (
        <>
          <ul>
            {data.results.map((activity) => (
              <Entry
                key={activity.id}
                activity={activity}
                call={calls.data?.get(activity.id)}
                awaitingCall={activity.source_model === CALL_SOURCE && calls.isLoading}
                // On a contact's own history every row is the same person.
                showContact={scope.on !== "contact"}
              />
            ))}
          </ul>
          <Pagination
            page={page}
            count={data.count}
            shown={data.results.length}
            isFetching={isFetching}
            onChange={setPage}
            pageSize={TIMELINE_PAGE_SIZE}
          />
        </>
      )}
    </Card>
  );
}
